'use client';
import React, { useEffect, useRef } from 'react';

interface FaqItem {
  question: string;
  answer: string;
  beforeSrc: string;
  afterSrc: string;
  beforeAlt: string;
  afterAlt: string;
  microCta: string;
  tag: string;
}

interface FaqZigzagProps {
  item: FaqItem;
  index: number;
  onCta: () => void;
}

export default function FaqZigzag({ item, index, onCta }: FaqZigzagProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.classList.add('hidden-initial');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.remove('hidden-initial');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="z-reveal py-5 md:py-6">
      <div
        style={{
          background: '#0d0d2b',
          border: '1px solid #1a1a4e',
          padding: '24px',
        }}
      >
        {/* Tag row */}
        <div className="flex items-center gap-2 mb-3">
          <span
            style={{
              background: 'rgba(0,245,255,0.08)',
              color: '#00f5ff',
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 900,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              padding: '3px 10px',
              border: '1px solid rgba(0,245,255,0.3)',
            }}
          >
            {item.tag}
          </span>
          <span
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 700,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: '#7b8ab8',
            }}
          >
            FAQ #{index + 1}
          </span>
        </div>

        {/* Question */}
        <h3
          style={{
            fontFamily: 'Barlow Condensed, Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(20px, 3vw, 28px)',
            textTransform: 'uppercase',
            color: '#e0e8ff',
            lineHeight: 1.1,
            letterSpacing: '0.01em',
            marginBottom: '12px',
          }}
        >
          {item.question}
        </h3>

        {/* Divider */}
        <div className="z-divider-light mb-4" />

        {/* Answer */}
        <p style={{ fontSize: '14px', color: '#a0b0d8', lineHeight: 1.7, marginBottom: '16px' }}>
          {item.answer}
        </p>

        {/* CTA */}
        <button
          onClick={onCta}
          className="z-btn-primary inline-flex"
          style={{ fontSize: '12px', padding: '10px 20px' }}
        >
          {item.microCta}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="ml-1">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>
  );
}